import React from 'react';
import { Box, alpha, useMediaQuery, useTheme } from '@mui/material';
import DeviceUIAction from './DeviceUIAction';
import DeviceHistoryAction from './DeviceHistoryAction';
import DeviceCloudConnectivityIcon from './DeviceCloudConnectivityIcon';
import DeviceInfoAction from './DeviceInfoAction';
import DeviceShareAction from './DeviceShareAction';
import { Device } from '../../../../../../redux/commander/api/device.model';
import {
  ButtonIcon,
  DefaultMenuButtons,
  MenuPrimary,
} from '../../../../../../theme/components';
import ActionsIcon from '../../../../../../theme/icons/ActionsIcon';
import DeviceSettingsAction from './DeviceSettingsAction';
import DeviceAlarmsAction from './DeviceAlarmsAction';
import { useAppSelector } from '../../../../../../redux/store.model';
import { getFeatureToggle } from '../../../../../../featureToggle';
import { deviceButtonsRightsSelector } from '../../../../../../redux/commander/selectors/devicesRightsSelectors';

export interface DeviceActionProps {
  device: Device;
  groupId?: string;
  variant?: 'icon' | 'menuItem';
}
export function DeviceActionsList(props: DeviceActionProps) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexGrow: 1,
        alignContent: 'center',
        justifyContent: 'flex-end',
        fontSize: '1.8em',
        gap: '1.2em',
        color: (theme) => theme.typography.body1.color,
      }}
    >
      <DeviceSettingsAction {...props} />
      <DeviceUIAction {...props} />
      <DeviceShareAction {...props} />
      <DeviceHistoryAction {...props} />
      <DeviceAlarmsAction {...props} />
      <DeviceCloudConnectivityIcon {...props} />
      <DeviceInfoAction {...props} />
    </Box>
  );
}

export function DeviceActionsGrid(props: DeviceActionProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const variant = isMobile ? 'menuItem' : 'icon';

  const extraButtons = isMobile
    ? [
        <DeviceShareAction
          key="DeviceShareAction"
          {...props}
          variant="menuItem"
        />,
        <DeviceInfoAction
          key="DeviceInfoAction"
          {...props}
          variant="menuItem"
        />,
      ]
    : [<DeviceActionsMenu key="DeviceActionsMenu" {...props} />];

  const menuButtons = [
    <DeviceCloudConnectivityIcon
      key="DeviceCloudConnectivityIcon"
      {...props}
    />,
    <DeviceSettingsAction
      key="DeviceSettingsAction"
      variant={variant}
      {...props}
    />,
    <DeviceUIAction key="DeviceUIAction" variant={variant} {...props} />,
    <DeviceAlarmsAction
      key="DeviceAlarmsAction"
      variant={variant}
      {...props}
    />,
    <DeviceHistoryAction
      key="DeviceHistoryAction"
      variant={variant}
      {...props}
    />,
    ...extraButtons,
  ];
  return isMobile ? (
    <DefaultMenuButtons buttons={menuButtons} />
  ) : (
    <Box
      sx={{
        flexGrow: 1,
        display: 'flex',
        justifyContent: 'flex-end',
        alignContent: 'center',
        fontSize: '1.6em',
        color: (theme) => theme.typography.body1.color,
      }}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
    >
      {menuButtons.map((button) => button)}
    </Box>
  );
}

export function DeviceActionsMenu(props: DeviceActionProps) {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (e: React.MouseEvent) => {
    setAnchorEl(null);
  };
  const featureToggle = useAppSelector(getFeatureToggle);
  const showDeviceInfo = useAppSelector(
    (state) =>
      deviceButtonsRightsSelector(state, {
        deviceId: props.device?.uuid,
        groupId: props.groupId,
      }).showDeviceInfo
  );

  if (!featureToggle.shareDevice && !showDeviceInfo) return null;

  return (
    <>
      <ButtonIcon
        onClick={handleClick}
        aria-controls={open ? 'actions-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        variant="text"
        sx={{
          bgcolor: (theme) =>
            open ? alpha(theme.palette.primary.main, 0.1) : undefined,
        }}
      >
        <ActionsIcon />
      </ButtonIcon>

      <MenuPrimary
        anchorEl={anchorEl}
        id="actions-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
      >
        <DeviceShareAction {...props} variant="menuItem" />
        <DeviceInfoAction {...props} variant="menuItem" />
      </MenuPrimary>
    </>
  );
}
